/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useEffect, useRef } from "react";
import { BEM } from "@adapptio/lib-components";
import { ButtonBase } from "../ButtonBase";
import { overrideForwardRef } from "../OverridableComponent";
import { useForkRef } from "../../../hooks";
import { isUndefined } from "@adapptio/lib-shared";

import "./MenuItem.scss";

export interface IMenuItemProps {
	children?: React.ReactNode;
	className?: string;
	autoFocus?: boolean;
	selected?: boolean;
	disabled?: boolean;
}

export const MenuItem = overrideForwardRef<IMenuItemProps, "li">(
	({ children, Component, className, autoFocus, selected, disabled, ...props }, ref) => {
		const baseClass = BEM("edt-menu-item", null, {
			selected,
			disabled
		});

		const itemRef = useRef<HTMLLIElement>();
		const forkRef = useForkRef(ref, itemRef);

		useEffect(() => {
			if (autoFocus) {
				itemRef.current?.focus();
			}
		}, [ autoFocus ]);

		if (className) {
			baseClass.add(className);
		}

		let tabIndex;
		if (!disabled) {
			tabIndex = isUndefined(props.tabIndex) ? -1 : props.tabIndex;
		}

		return (
			<ButtonBase
				{...props}
				disabled={disabled}
				Component={Component}
				className={baseClass.toClassName()}
				tabIndex={tabIndex}
				ref={forkRef}
			>
				{children}
			</ButtonBase>
		);
	}
);

MenuItem.defaultProps = {
	Component: "li"
};
